import { LessonCard } from "@/components/lessons/LessonCard";
import { DeleteModal } from "@/components/modal/DeleteModal";
import { RestoreAllModal } from "@/components/modal/RestoreAllModal";
import { TrashSelectionActionModal } from "@/components/modal/TrashSelectionActionModal";
import { UnlinkModal } from "@/components/modal/UnlinkModal";
import { ProjectCard } from "@/components/projects/ProjectCard";
import { MainSidebar } from "@/components/sidebars/MainSidebar";
import { LessonCardSkeleton, ProjectCardSkeleton } from "@/components/skeletons";
import { SelectionProvider, useSelection } from "@/contexts/SelectionContext";
import { LessonFieldsFragmentDoc } from "@/generated/graphql";
import { ITEM_LIMIT } from "@/lib/constants/shared";
import { useLessonsByParent } from "@/lib/hooks/lessons/useLessonsByParent";
import { useProjectsByParent } from "@/lib/hooks/projects/useProjectsByParent";
import type { LessonFields } from "@/types/Lesson";
import type { ProjectFields } from "@/types/Project";

import { Button, IconButton, LoadMoreButton } from "@ai-teach/ui-web";
import { useFragment } from "@apollo/client/react";
import { Link } from "@tanstack/react-router";
import { IoIosArrowRoundBack } from "react-icons/io";
import { MdDelete, MdRestore } from "react-icons/md";
import { RiDeleteBin6Fill } from "react-icons/ri";

interface ProjectTrashPageProps {
  projectId: string | null;
  displayName: string;
}

function ProjectTrashPageContent({ projectId, displayName }: ProjectTrashPageProps) {
  const {
    clearAllSelections,
    selectProjects,
    selectLessons,
    toggleRestoreModal,
    toggleDeleteModal,
    isRestoreModalOpen,
    isDeleteModalOpen,
    isSelectionMode,
    isUnlinkModalOpen,
  } = useSelection();

  const {
    projects: trashedProjects,
    hasMore: projectsHasMore,
    loading: projectsLoading,
    loadMore: loadMoreProjects,
  } = useProjectsByParent({
    parentId: projectId,
    searchText: "",
    isTrash: true,
  });

  const {
    lessons: trashedLessons,
    hasMore: lessonsHasMore,
    loading: lessonsLoading,
    loadMore: loadMoreLessons,
  } = useLessonsByParent({
    parentId: projectId,
    searchText: "",
    isTrash: true,
  });

  // Only the items already fetched are acted on; "all" means everything currently listed.
  const selectAll = (type: "Projects" | "Lessons") => {
    clearAllSelections();

    if (type === "Projects") {
      const items = trashedProjects.map((project) => ({
        id: project.id,
        isLinkedCopy: project.isLinkedCopy ?? false,
      }));
      selectProjects(items);
      return items.length > 0;
    }

    const items = trashedLessons.map((lesson) => ({
      id: lesson.id,
      isLinkedCopy: lesson.isLinkedCopy ?? false,
    }));
    selectLessons(items);
    return items.length > 0;
  };

  const handleRestoreAll = (type: "Projects" | "Lessons") => {
    if (selectAll(type)) toggleRestoreModal(type);
  };

  const handleDeleteAll = (type: "Projects" | "Lessons") => {
    if (selectAll(type)) toggleDeleteModal(true);
  };

  return (
    <div className="flex h-screen">
      <MainSidebar />

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <div className="max-w-7xl mx-auto p-6">
          {/* Back Navigation */}
          {projectId ? (
            <Link
              to="/admin/project/$projectId"
              params={{ projectId }}
              className="text-gray-500 flex items-center gap-1"
            >
              <IoIosArrowRoundBack fontSize={30} /> {displayName}
            </Link>
          ) : (
            <Link to="/admin" className="text-gray-500 flex items-center gap-1">
              <IoIosArrowRoundBack fontSize={30} /> Home
            </Link>
          )}

          {/* Page Title */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="h3 flex-1 text-center">{displayName ? displayName : "Home"} Trash</h1>
          </div>

          {/* Trashed Projects Section */}
          {trashedProjects.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center justify-between">
                <h2 className="h4 mb-6">Trashed Projects</h2>
                <div className="flex items-center gap-3">
                  {/* Restore All Projects Button */}
                  <Button
                    type="button"
                    onClick={() => handleRestoreAll("Projects")}
                    variant="primary"
                    className="group hover:scale-105"
                  >
                    <MdRestore className="text-xl group-hover:rotate-[-15deg] transition-transform duration-200" />
                    <span>Restore All</span>
                  </Button>
                  {/* Delete All Projects Button */}
                  <Button
                    type="button"
                    onClick={() => handleDeleteAll("Projects")}
                    variant="danger"
                    className="group hover:scale-105"
                  >
                    <MdDelete className="text-xl" />
                    <span>Delete All</span>
                  </Button>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                {projectsLoading && trashedProjects.length === 0 ? (
                  <>
                    {Array.from({ length: ITEM_LIMIT.generalItemLimit }).map((_, i) => (
                      <ProjectCardSkeleton key={`project-skeleton-card-${i + 1}`} viewMode="grid" />
                    ))}
                  </>
                ) : (
                  trashedProjects.map((project) => (
                    <ProjectCard
                      key={project.id}
                      project={project as ProjectFields}
                      viewMode="grid"
                      url="trash"
                      isSelectionMode={isSelectionMode}
                    />
                  ))
                )}
              </div>
              <LoadMoreButton
                onClick={loadMoreProjects}
                hasMore={projectsHasMore}
                loading={projectsLoading}
              />
            </div>
          )}

          {/* Trashed Lessons Section */}
          {trashedLessons.length > 0 && (
            <div className="mb-12">
              <div className="flex items-center justify-between">
                <h2 className="h4 mb-6">Trashed Lessons</h2>
                <div className="flex items-center gap-3">
                  {/* Restore All Lessons Button */}
                  <Button
                    type="button"
                    onClick={() => handleRestoreAll("Lessons")}
                    variant="primary"
                    className="group hover:scale-105"
                  >
                    <MdRestore className="text-xl group-hover:rotate-[-15deg] transition-transform duration-200" />
                    <span>Restore All</span>
                  </Button>
                  {/* Delete All Lessons Button */}
                  <Button
                    type="button"
                    onClick={() => handleDeleteAll("Lessons")}
                    variant="danger"
                    className="group hover:scale-105"
                  >
                    <MdDelete className="text-xl" />
                    <span>Delete All</span>
                  </Button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-6 items-start">
                {lessonsLoading && trashedLessons.length === 0 ? (
                  <>
                    {Array.from({ length: ITEM_LIMIT.generalItemLimit }).map((_, i) => (
                      <LessonCardSkeleton key={`lesson-skeleton-card-${i + 1}`} viewMode="grid" />
                    ))}
                  </>
                ) : (
                  trashedLessons.map((lesson, index) => (
                    <LessonCard
                      key={lesson.id}
                      lesson={lesson as LessonFields}
                      viewMode="grid"
                      url="trash"
                      isSelectionMode={isSelectionMode}
                      index={index}
                    />
                  ))
                )}
              </div>
              <LoadMoreButton
                onClick={loadMoreLessons}
                hasMore={lessonsHasMore}
                loading={lessonsLoading}
              />
            </div>
          )}

          {/* Empty state */}
          {trashedProjects.length === 0 && trashedLessons.length === 0 && !projectsLoading && (
            <div className="flex flex-col justify-center items-center min-h-[230px] gap-2">
              <div className="text-6xl text-gray-300">
                <RiDeleteBin6Fill />
              </div>
              <p className="text-xl font-medium card-title">Trash is empty</p>
            </div>
          )}
        </div>
      </div>

      {/* Modals */}
      {isRestoreModalOpen && (
        <RestoreAllModal isOpen={true} modalType={isRestoreModalOpen} sourceId={projectId} />
      )}
      {isDeleteModalOpen && <DeleteModal isOpen={isDeleteModalOpen} />}
      {isSelectionMode && <TrashSelectionActionModal page="projectTrashPage" />}
      {isUnlinkModalOpen && <UnlinkModal isOpen={true} />}
    </div>
  );
}

export function ProjectTrashPage(props: ProjectTrashPageProps) {
  return (
    <SelectionProvider>
      <ProjectTrashPageContent {...props} />
    </SelectionProvider>
  );
}
